'use client'

import { useEffect, useRef } from 'react'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { ArrowTopRightOnSquareIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import { Review } from './reviews-data'

const SWIPE_PX = 50

interface ReviewLightboxProps {
  reviews: Review[]
  /** Index of the open review, or null when the dialog is closed. */
  index: number | null
  onChange: (index: number) => void
  onClose: () => void
}

/** Full-size review screenshot with previous/next (buttons, arrow keys, swipe) that wraps around the list. */
const ReviewLightbox = ({ reviews, index, onChange, onClose }: ReviewLightboxProps) => {
  const { t } = useTranslation()
  const touchStartX = useRef<number | null>(null)
  const open = index !== null && reviews.length > 0
  const current = open ? index : 0
  const review = reviews[current]
  const total = reviews.length

  const go = (step: number) => onChange((current + step + total) % total)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') onChange((current - 1 + total) % total)
      if (event.key === 'ArrowRight') onChange((current + 1) % total)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, current, total, onChange])

  if (!review) return null

  const neighbours = total > 1 ? [reviews[(current + 1) % total], reviews[(current - 1 + total) % total]] : []
  const navButtonClassName = 'flex size-12 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white'

  return (
    <Dialog open={open} onClose={onClose} className='relative z-[300]'>
      <div className='fixed inset-0 bg-slate-950/90 backdrop-blur-sm' aria-hidden='true' />
      <div className='fixed inset-0 flex items-center justify-center p-4'>
        <DialogPanel className='flex w-full max-w-3xl flex-col items-center gap-3'>
          <DialogTitle className='sr-only'>{review.alt}</DialogTitle>

          <div className='flex w-full max-w-lg items-center justify-between gap-3'>
            {review.url ? (
              <a href={review.url} target='_blank' rel='noopener noreferrer' className='inline-flex items-center gap-1.5 text-sm font-medium text-white/80 underline-offset-4 hover:text-white hover:underline'>
                {t('Landing.Reviews.OpenOriginal')}
                <ArrowTopRightOnSquareIcon className='size-4' aria-hidden />
              </a>
            ) : (
              <span />
            )}
            <button type='button' onClick={onClose} aria-label={t('Landing.Reviews.Close')} className='flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white'>
              <XMarkIcon className='size-6' />
            </button>
          </div>

          <div
            className='flex w-full min-w-0 justify-center'
            onTouchStart={event => (touchStartX.current = event.touches[0].clientX)}
            onTouchEnd={event => {
              if (touchStartX.current === null) return
              const dx = event.changedTouches[0].clientX - touchStartX.current
              touchStartX.current = null
              if (Math.abs(dx) > SWIPE_PX) go(dx < 0 ? 1 : -1)
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              key={review.path}
              src={review.path}
              alt={review.alt}
              width={850}
              height={1020}
              draggable={false}
              className='h-auto max-h-[calc(100dvh-10rem)] w-auto max-w-full select-none rounded-2xl bg-white object-contain shadow-2xl'
            />
          </div>

          {total > 1 && (
            <div className='flex items-center justify-center gap-6'>
              <button type='button' onClick={() => go(-1)} aria-label={t('Landing.Reviews.Previous')} className={navButtonClassName}>
                <ChevronLeftIcon className='size-6' />
              </button>
              <span aria-live='polite' className='min-w-14 text-center text-base font-semibold tabular-nums text-white'>
                {current + 1} / {total}
              </span>
              <button type='button' onClick={() => go(1)} aria-label={t('Landing.Reviews.Next')} className={navButtonClassName}>
                <ChevronRightIcon className='size-6' />
              </button>
            </div>
          )}

          {/* Preload the neighbours so swiping shows the next screenshot immediately. */}
          {neighbours.map(item => (
            // eslint-disable-next-line @next/next/no-img-element
            <img key={item.path} src={item.path} alt='' aria-hidden className='hidden' />
          ))}
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default ReviewLightbox
